/**
 * OwnerEditMenuItemModal
 * ──────────────────────
 * Side-drawer style dialog letting a restaurant owner edit a single menu
 * item live in front of an investor.
 *
 * Wired to:
 *   PATCH /api/directory/restaurants/:slug/menu-items/:id
 *   PUT   /api/directory/restaurants/:slug/menu-items/:id/ingredients
 *   GET   /api/directory/restaurants/:slug/menu-items/:id/ingredients
 *   DELETE /api/directory/restaurants/:slug/menu-items/:id
 *
 * Owner mode is toggled by `?owner=1` on the restaurant URL.
 */
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, X, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
export default function OwnerEditMenuItemModal({ slug, item, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [method, setMethod] = useState("");
  const [isPublic, setIsPublic] = useState(true);
  const [isInternal, setIsInternal] = useState(false);
  const [ingredients, setIngredients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Hydrate when item changes.
  useEffect(() => {
    if (!item) return;
    setName(item.name || "");
    setDescription(item.description || "");
    const p = item.price ?? (item.prices && (typeof item.prices === "object"
      ? Object.values(item.prices)[0] : item.prices[0])) ?? "";
    setPrice(String(p ?? ""));
    setMethod(item.method || "");
    setIsPublic(item.is_public !== false);
    setIsInternal(item.is_internal === true);

    // Try to load attached ingredients (recipe-derived items) if id is numeric.
    if (item.id != null && /^\d+$/.test(String(item.id))) {
      setLoading(true);
      fetch(`${API}/api/directory/restaurants/${slug}/menu-items/${item.id}/ingredients`)
        .then((r) => r.json())
        .then((d) => setIngredients(Array.isArray(d.ingredients) ? d.ingredients : []))
        .catch(() => setIngredients([]))
        .finally(() => setLoading(false));
    } else if (Array.isArray(item.ingredients)) {
      setIngredients(item.ingredients.map((ing) =>
        typeof ing === "string" ? { name: ing, qty: "", unit: "each" } : ing));
    } else {
      setIngredients([]);
    }
  }, [item, slug]);

  const updateIngredient = (idx, field, value) =>
    setIngredients((rows) => rows.map((r, i) => i === idx ? { ...r, [field]: value } : r));

  const addIngredient = () =>
    setIngredients((rows) => [...rows, { name: "", qty: "", unit: "each" }]);

  const removeIngredient = (idx) =>
    setIngredients((rows) => rows.filter((_, i) => i !== idx));

  const handleSave = async () => {
    if (!item?.id) {
      toast.error("Missing item id — can't save");
      return;
    }
    setSaving(true);
    try {
      // 1. Patch core fields
      const patchBody = {
        name: name.trim(),
        description: description.trim(),
        price: price.trim(),
        method: method.trim(),
        is_public: isPublic,
        is_internal: isInternal,
      };
      const patchRes = await fetch(
        `${API}/api/directory/restaurants/${slug}/menu-items/${item.id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(patchBody),
        },
      );
      if (!patchRes.ok) throw new Error(`Patch failed: ${patchRes.status}`);

      // 2. Replace ingredients if numeric id
      if (/^\d+$/.test(String(item.id))) {
        const cleanedIngs = ingredients
          .filter((r) => r.name && String(r.name).trim())
          .map((r) => ({ name: r.name.trim(), qty: r.qty || "", unit: r.unit || "each" }));
        const putRes = await fetch(
          `${API}/api/directory/restaurants/${slug}/menu-items/${item.id}/ingredients`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ ingredients: cleanedIngs }),
          },
        );
        if (!putRes.ok) throw new Error(`Ingredients save failed: ${putRes.status}`);
      }

      toast.success("Menu item saved");
      onSaved?.({ ...item, name, description, price, method, is_public: isPublic, is_internal: isInternal });
      onClose?.();
    } catch (e) {
      toast.error(e.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!item?.id) return;
    if (!window.confirm(`Delete "${item.name}" permanently?`)) return;
    setSaving(true);
    try {
      const res = await fetch(
        `${API}/api/directory/restaurants/${slug}/menu-items/${item.id}`,
        { method: "DELETE" },
      );
      if (!res.ok) throw new Error(`Delete failed: ${res.status}`);
      toast.success("Item deleted");
      onSaved?.({ deleted: true, id: item.id });
      onClose?.();
    } catch (e) {
      toast.error(e.message || "Failed to delete");
    } finally {
      setSaving(false);
    }
  };

  if (!item) return null;

  return (
    <Dialog open={!!item} onOpenChange={(open) => !open && onClose?.()}>
      <DialogContent
        data-testid="owner-edit-menu-item-modal"
        className="max-w-xl p-0 overflow-hidden gap-0 max-h-[92vh] flex flex-col bg-white text-stone-900"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-stone-200 bg-stone-50">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-amber-700 font-bold">
              Owner edit · live
            </p>
            <DialogTitle className="text-lg font-bold text-stone-900 truncate">
              {item.name || "Untitled item"}
            </DialogTitle>
          </div>
          <button
            data-testid="owner-edit-close"
            onClick={() => onClose?.()}
            className="w-8 h-8 rounded-full hover:bg-stone-200 grid place-items-center"
            aria-label="Close"
          >
            <X className="w-4 h-4" strokeWidth={2} />
          </button>
        </div>

        <div className="overflow-y-auto px-5 py-4 space-y-4">
          <div>
            <Label htmlFor="oe-name" className="text-[11px] uppercase tracking-wider font-bold text-stone-600">Name</Label>
            <Input
              id="oe-name"
              data-testid="owner-edit-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="oe-desc" className="text-[11px] uppercase tracking-wider font-bold text-stone-600">Description</Label>
            <Textarea
              id="oe-desc"
              data-testid="owner-edit-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="oe-price" className="text-[11px] uppercase tracking-wider font-bold text-stone-600">Price (AUD)</Label>
            <Input
              id="oe-price"
              data-testid="owner-edit-price"
              type="text"
              inputMode="decimal"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              className="mt-1 tabular-nums max-w-[160px]"
              placeholder="e.g. 18.50"
            />
          </div>

          {/* Ingredients */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-[11px] uppercase tracking-wider font-bold text-stone-600">
                Ingredients
              </Label>
              <Button
                size="sm"
                variant="outline"
                onClick={addIngredient}
                data-testid="owner-edit-add-ingredient"
                className="h-7 px-2 text-[12px]"
              >
                <Plus className="w-3 h-3 mr-1" /> Add row
              </Button>
            </div>
            {loading ? (
              <p className="text-[12px] text-stone-500 italic flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" /> Loading…
              </p>
            ) : ingredients.length === 0 ? (
              <p className="text-[12px] text-stone-500 italic">No ingredients yet — tap "Add row" to insert one.</p>
            ) : (
              <ul className="space-y-1.5" data-testid="owner-edit-ingredient-list">
                {ingredients.map((row, idx) => (
                  <li key={idx} className="flex items-center gap-1.5">
                    <Input
                      data-testid={`owner-edit-ing-name-${idx}`}
                      value={row.name || ""}
                      onChange={(e) => updateIngredient(idx, "name", e.target.value)}
                      placeholder="Ingredient"
                      className="flex-1"
                    />
                    <Input
                      data-testid={`owner-edit-ing-qty-${idx}`}
                      value={row.qty || ""}
                      onChange={(e) => updateIngredient(idx, "qty", e.target.value)}
                      placeholder="Qty"
                      className="w-20 tabular-nums"
                    />
                    <Input
                      data-testid={`owner-edit-ing-unit-${idx}`}
                      value={row.unit || "each"}
                      onChange={(e) => updateIngredient(idx, "unit", e.target.value)}
                      placeholder="Unit"
                      className="w-20"
                    />
                    <button
                      data-testid={`owner-edit-ing-delete-${idx}`}
                      onClick={() => removeIngredient(idx)}
                      className="w-8 h-8 rounded-full text-red-600 hover:bg-red-50 grid place-items-center shrink-0"
                      aria-label="Remove"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Method */}
          <div>
            <Label htmlFor="oe-method" className="text-[11px] uppercase tracking-wider font-bold text-stone-600">
              Method / preparation notes
            </Label>
            <Textarea
              id="oe-method"
              data-testid="owner-edit-method"
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              rows={5}
              className="mt-1 font-mono text-[12px]"
              placeholder="Step-by-step prep notes (kitchen-facing)"
            />
          </div>

          {/* Visibility */}
          <div className="rounded-lg border border-stone-200 bg-stone-50 p-3 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label htmlFor="oe-public" className="text-[13px] font-semibold text-stone-900">
                  Show on public menu
                </Label>
                <p className="text-[11px] text-stone-500">Customers see this dish on the live site.</p>
              </div>
              <Switch
                id="oe-public"
                data-testid="owner-edit-is-public"
                checked={isPublic}
                onCheckedChange={setIsPublic}
              />
            </div>
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label htmlFor="oe-internal" className="text-[13px] font-semibold text-stone-900">
                  Staff / internal only
                </Label>
                <p className="text-[11px] text-stone-500">Hide from customers but keep in the back-office menu.</p>
              </div>
              <Switch
                id="oe-internal"
                data-testid="owner-edit-is-internal"
                checked={isInternal}
                onCheckedChange={setIsInternal}
              />
            </div>
          </div>
        </div>

        {/* Footer actions */}
        <div className="border-t border-stone-200 px-5 py-3 bg-white flex items-center justify-between gap-2 shrink-0">
          <Button
            variant="ghost"
            onClick={handleDelete}
            disabled={saving}
            data-testid="owner-edit-delete"
            className="text-red-600 hover:bg-red-50 hover:text-red-700"
          >
            <Trash2 className="w-4 h-4 mr-1" /> Delete
          </Button>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => onClose?.()}
              disabled={saving}
              data-testid="owner-edit-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || !name.trim()}
              data-testid="owner-edit-save"
              className="bg-stone-900 hover:bg-stone-800 text-white"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-1" />
              )}
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
